import { html, LitElement, css } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import * as Converters from '../../../src/converters/converters';
import { VoyagePlan } from '../../../src/models/vessel-planning-model';
import { downloadPlanWithSelectedRoute, downloadBasicPlanWithSelectedRoute } from '../../../src/utils/utils';
import { Format } from '../../../src/converters/types/converters.types';

@customElement('voyage-route-export')
export class VoyageRouteExport extends LitElement {
    @property({type: Boolean}) collapsed: boolean = true;
    @property({type: Boolean, attribute: "show-above"}) showAbove: boolean = false;
    @property({type: Object}) plan: VoyagePlan|null = null;
    @property({type: Function}) editRouteFn!: (routeId: string) => Promise<void>;

    static get styles() {
        return [
            css`
            :host {
                display: block;
                width: 328px;
            }

            .menu {
                display: flex;
                align-items: center;
                justify-content: center;
                height: 30px;
                width: 100%;
                background: var(--primary-blue-05, blue);
                color: var(--neutrals-grey-10, white);
                border-radius: 3px;

                font-family: var(--component-heading-ff, 'serif');
                font-size: 16px;
                line-height: 24px;

                cursor: pointer;
            }

            .menu > svg {
                display: block;
                fill: currentColor;
                height: 16px;
            }

            .collapsible-menu {
                width: 100%;
                display: flex;
                flex-direction: column;
            }

            .item {
                display: flex;
                align-items: center;
                height: 39px;
                width: 100%;
                padding-left: 25px;
                padding-top: 5px;
                padding-bottom: 5px;
                border: none;
                text-align: left;
                font-family: MaerskText;
                font-size: 14px;
                background: var(--neutrals-grey-10, white);
                color: var(--neutrals-grey-02, black);
                fill: currentColor;
                cursor: pointer;
            }

            .item:hover {
                height: 39px;
                background: var(--neutrals-grey-08, white);
            }

            .item > svg {
                display: block;
                margin-right: 5px;
                height: 14px;
            }

            .menu-content {
                position: absolute;
                top: 0;
                right: 0;
                left: 0;
                padding-top: 5px;
                background: var(--neutrals-grey-10, white);
                border: 1px solid var(--neutrals-grey-06, grey);
                border-radius: 0 0 2.5px 2.5px;
                z-index: 1;
            }

            .menu-content[show-above] {
                top: unset;
                bottom: 0;
                border-radius: 2.5px 2.5px 0 0;
                box-shadow: 2px -2px 4px rgba(0,0,0,0.2);
            }

            .positioner {
                position: relative;
                height: 0px;
            }
        `];
    }

    render() {

        const menuContent = html`
            <div class="positioner">
                <div class="menu-content" ?hidden=${this.collapsed} ?show-above=${this.showAbove}>
                    <button class="item" data-test="VoyagePlanning" @click=${() => this._exportPlan(Format.BasicPlan)}>${SvgIcons.solidFile} Save as Basic Plan</button>
                    <button class="item" data-test="NACOS" @click=${() => this._exportPlan(Format.Nacos)}>${SvgIcons.solidFile} Export to NACOS</button>
                    <button class="item" data-test="Sperry" @click=${() => this._exportPlan(Format.Sperry)}>${SvgIcons.solidFile} Export to Sperry</button>
                    <button class="item" data-test="SPOS" @click=${() => this._exportPlan(Format.SPOS)}>${SvgIcons.solidFile} Export to SPOS ( Berth to Berth ) </button>
                    <button class="item" data-test="SPOS" @click=${() => this._exportPlan(Format.SPOS_Pilot)}>${SvgIcons.solidFile} Export to SPOS ( Pilot to Pilot )</button>
                    <button class="item" data-test="JRC" @click=${() => this._exportPlan(Format.JRC)}>${SvgIcons.solidFile} Export to JRC</button>
                    <button class="item" data-test="RTZ_1.0" @click=${() => this._exportPlan(Format.RTZ_1_0)}>${SvgIcons.solidFile} Export to RTZ 1.0</button>
                    <button class="item" data-test="RTZ_1.2" @click=${() => this._exportPlan(Format.RTZ_1_2)}>${SvgIcons.solidFile} Export to RTZ 1.2</button>
                    <button class="item" data-test="VoyagePlanning" @click=${() => this._exportPlan(Format.VoyagePlanning)}>${SvgIcons.solidFile} Save Voyage Plan</button>
                </div>
            </div>
        `;

        return html`
            <div class="collapsible-menu">
                ${this.showAbove ? menuContent : html``}
                <mc-button appearance="neutral" fit="small" width="full-width" class="menu" data-test="route-download-btn" icon="tray-arrow-down"
                    @click=${(e : Event) => this._handleDownloadRoute(e)}>Download route
                </mc-button>
                ${!this.showAbove ? menuContent : html``}
            </div>
        `;
    }

    private _handleDownloadRoute(e: Event) {
        e.stopPropagation();
        this.collapsed = !this.collapsed;
    }

    saveFile(name: string, mime: string, str: string): void {
        var blob = new Blob([str], {type: mime});

        const exportElement = document.createElement('a');
        exportElement.download = name;
        exportElement.href = URL.createObjectURL(blob);
        exportElement.click();
    }

    private async _exportPlan(format: Format) {
        if (!this.plan || !this.plan.currentSelectedRouteId) return;
        if(format === Format.BasicPlan) {
            downloadBasicPlanWithSelectedRoute({ plan :this.plan, format, editRouteFn: this.editRouteFn });
        } else {
            downloadPlanWithSelectedRoute(this.plan, format);
        }
    }
}
